import api from './api';
import logger from '../utils/logger';

/**
 * Payment Service
 * @description Handles all Razorpay payment operations
 */
class PaymentService {
    /**
     * Create a new payment order
    * @param {object} payload
    * @param {number} [payload.amount] - Amount in INR (optional when planId provided)
    * @param {'basic'|'advance'|'pro'} [payload.planId]
    * @param {'monthly'|'annually'} [payload.billingCycle]
    * @param {object} [payload.notes]
    * @param {number|string} [payload.userId]
    * @param {string} [payload.userEmail]
    * @param {string} [payload.userPhone]
     * @returns {Promise<object>} Order details with orderId, keyId, etc.
     */
    async createOrder({
        amount,
        planId,
        billingCycle,
        addonAmount = 0,
        notes = {},
        userId,
        userEmail,
        userPhone
    }) {
        try {
            logger.log('[PaymentService] Creating order:', { amount, planId, billingCycle, notes });

            const payload = {
                notes
            };

            // Ensure backend-required receipt field exists
            payload.receipt = `receipt_${Date.now()}`;

            if (amount) payload.amount = amount;
            if (planId) payload.planId = planId;
            if (billingCycle) payload.billingCycle = billingCycle;
            if (addonAmount && addonAmount > 0) payload.addonAmount = addonAmount;
            if (userId) payload.userId = userId;
            if (userEmail) payload.userEmail = userEmail;
            if (userPhone) payload.userPhone = userPhone;

            const response = await api.post('/customer/payment/create-order', payload);

            if (response.success) {
                logger.log('[PaymentService] Order created successfully:', response.data.orderId);
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to create order');
            }
        } catch (error) {
            logger.error('[PaymentService] Error creating order:', error);
            // Development fallback: try to get a mock order from backend to open checkout
            try {
                logger.log('[PaymentService] Attempting dev fallback: requesting mock order');
                const amountForMock = amount || 19999;
                const mockResp = await api.get(`/customer/payment/mock-order?amount=${encodeURIComponent(amountForMock)}`);
                if (mockResp && mockResp.success) {
                    logger.log('[PaymentService] Received mock order for dev testing:', mockResp.data.orderId || mockResp.data.orderId);
                    return mockResp.data;
                }
            } catch (mockErr) {
                logger.error('[PaymentService] Mock order fallback failed:', mockErr);
            }
            // If api.request attached a parsed body, surface it for easier debugging
            if (error && error.body) {
                try {
                    const bodyStr = typeof error.body === 'object' ? JSON.stringify(error.body) : String(error.body);
                    logger.error('[PaymentService] createOrder response body:', bodyStr);
                    throw new Error(bodyStr);
                } catch (e) {
                    throw error;
                }
            }
            throw error;
        }
    }

    /**
     * Verify payment after Razorpay checkout
     * @param {object} paymentData - Payment response from Razorpay
     * @returns {Promise<object>} Verification result
     */
    async verifyPayment(paymentData) {
        try {
            logger.log('[PaymentService] Verifying payment:', paymentData.razorpay_order_id);

            const response = await api.post('/customer/payment/verify-payment', {
                order_id: paymentData.razorpay_order_id || paymentData.order_id,
                payment_id: paymentData.razorpay_payment_id || paymentData.payment_id,
                signature: paymentData.razorpay_signature || paymentData.signature
            });

            if (response.success) {
                logger.log('[PaymentService] Payment verified successfully');
                return response.data;
            } else {
                throw new Error(response.message || 'Payment verification failed');
            }
        } catch (error) {
            logger.error('[PaymentService] Payment verification error:', error);
            throw error;
        }
    }

    /**
     * Verify subscription payment after Razorpay subscription checkout
     * @param {object} params - { razorpay_subscription_id, razorpay_payment_id, razorpay_signature }
     */
    async verifySubscriptionPayment(params = {}) {
        try {
            const payload = {
                razorpay_subscription_id: params.razorpay_subscription_id || params.subscriptionId || params.subscription_id,
                razorpay_payment_id: params.razorpay_payment_id || params.razorpay_payment_id || params.razorpay_payment_id,
                razorpay_signature: params.razorpay_signature || params.signature || params.razorpay_signature
            };
            const response = await api.post('/customer/subscription/verify-payment', payload);
            if (response.success) return response.data;
            throw new Error(response.message || 'Failed to verify subscription payment');
        } catch (error) {
            logger.error('[PaymentService] verifySubscriptionPayment error:', error);
            throw error;
        }
    }

    /**
     * Get payment details by order ID
     * @param {string} orderId - Razorpay order ID
     * @returns {Promise<object>} Payment details
     */
    async getPaymentByOrderId(orderId) {
        try {
            logger.log('[PaymentService] Fetching payment:', orderId);

            const response = await api.get(`/customer/payment/order/${orderId}`);

            if (response.success) {
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to fetch payment');
            }
        } catch (error) {
            logger.error('[PaymentService] Error fetching payment:', error);
            throw error;
        }
    }

    /**
     * Get all payments for current user
     * @param {number} userId - User ID
     * @param {object} filters - Optional filters (status, limit, offset)
     * @returns {Promise<object>} Payment list with pagination
     */
    async getUserPayments(userId, filters = {}) {
        try {
            logger.log('[PaymentService] Fetching user payments:', userId);

            const queryParams = new URLSearchParams(filters).toString();
            const url = `/payments/user/${userId}${queryParams ? `?${queryParams}` : ''}`;

            const response = await api.get(url);

            if (response.success) {
                return {
                    payments: response.data,
                    total: response.total,
                    limit: response.limit,
                    offset: response.offset
                };
            } else {
                throw new Error(response.message || 'Failed to fetch payments');
            }
        } catch (error) {
            logger.error('[PaymentService] Error fetching user payments:', error);
            throw error;
        }
    }

    /**
     * Get all payments (admin)
     * @param {object} filters - Optional filters (status, limit, offset, q, userId)
     */
    async getAllPayments(filters = {}) {
        try {
            const queryParams = new URLSearchParams(filters).toString();
            const url = `/payments/all${queryParams ? `?${queryParams}` : ''}`;
            const response = await api.get(url);

            if (response.success) {
                return {
                    payments: response.data,
                    total: response.total,
                    limit: response.limit,
                    offset: response.offset
                };
            } else {
                throw new Error(response.message || 'Failed to fetch payments');
            }
        } catch (error) {
            logger.error('[PaymentService] Error fetching all payments:', error);
            throw error;
        }
    }

    /**
     * Fetch GMB profiles linked to the logged-in user (for checkout profile selection)
     * @returns {Promise<Array>} Array of profile objects
     */
    async getGmbProfiles() {
        try {
            const response = await api.get('/customer/gmb-account/profiles');

            if (response.success) {
                return response.data?.profiles || response.profiles || [];
            }

            throw new Error(response.message || 'Failed to fetch GMB profiles');
        } catch (error) {
            logger.error('[PaymentService] Error fetching GMB profiles:', error);
            throw error;
        }
    }

    /**
     * Check if user qualifies for multi-profile discount (10% off for 2nd+ GMB profile)
     * @param {number|string} userId - User ID
     * @param {string} userEmail - User email (fallback)
     * @returns {Promise<{eligible: boolean, existingProfiles: number, discountPercent: number}>}
     */
    async checkMultiProfileDiscount(userId, userEmail) {
        try {
            const response = await api.get('/customer/payment/check-multi-profile-discount', {
                params: { userId, userEmail }
            });

            if (response.success) {
                return {
                    eligible: response.data?.eligible || false,
                    existingProfiles: response.data?.existingProfiles || 0,
                    discountPercent: response.data?.eligible ? 10 : 0
                };
            }

            return { eligible: false, existingProfiles: 0, discountPercent: 0 };
        } catch (error) {
            logger.warn('[PaymentService] Error checking multi-profile discount:', error);
            // Don't fail payment flow, just return no discount
            return { eligible: false, existingProfiles: 0, discountPercent: 0 };
        }
    }

    /**
     * Load Razorpay SDK script
     * @returns {Promise<void>}
     */
    loadRazorpayScript() {
        return new Promise((resolve, reject) => {
            // Check if already loaded
            if (window.Razorpay) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.async = true;

            script.onload = () => {
                logger.log('[PaymentService] Razorpay SDK loaded');
                resolve();
            };

            script.onerror = () => {
                logger.error('[PaymentService] Failed to load Razorpay SDK');
                reject(new Error('Failed to load Razorpay SDK'));
            };

            document.body.appendChild(script);
        });
    }

    /**
     * Open Razorpay checkout
     * @param {object} options - Razorpay checkout options
     * @param {function} onSuccess - Success callback
     * @param {function} onFailure - Failure callback
     */
    async openCheckout(options, onSuccess, onFailure) {
        try {
            await this.loadRazorpayScript();

            const rzpOptions = {
                ...options,
                handler: function (response) {
                    logger.log('[PaymentService] Payment successful:', response);
                    onSuccess(response);
                },
                modal: {
                    ondismiss: function () {
                        logger.log('[PaymentService] Payment cancelled by user');
                        onFailure(new Error('Payment cancelled'));
                    }
                }
            };

            const rzp = new window.Razorpay(rzpOptions);
            rzp.open();

            rzp.on('payment.failed', function (response) {
                logger.error('[PaymentService] Payment failed:', response.error);
                onFailure(response.error);
            });

        } catch (error) {
            logger.error('[PaymentService] Error opening checkout:', error);
            onFailure(error);
        }
    }

    /**
     * Complete payment flow (create order + open checkout + verify)
     * @param {object} payload
     * @param {number} [payload.amount]
    * @param {'basic'|'advance'|'pro'} [payload.planId]
     * @param {'monthly'|'annually'} [payload.billingCycle]
     * @param {object} [payload.metadata]
     * @returns {Promise<object>} Payment result
     */
    async processPayment({ amount, planId, billingCycle, metadata = {}, addonAmount = 0 }) {
        try {
            // Step 1: Create order
            const notePayload = metadata.notes || {};
            const orderData = await this.createOrder({
                amount,
                planId,
                billingCycle,
                addonAmount,
                notes: notePayload,
                userId: metadata.userId || notePayload.user_id || notePayload.userId,
                userEmail: metadata.userEmail || notePayload.user_email,
                userPhone: metadata.userPhone || notePayload.user_phone
            });

            // Step 2: Open Razorpay checkout
            return new Promise((resolve, reject) => {
                const options = {
                    key: orderData.keyId,
                    amount: orderData.amount * 100, // Convert to paise
                    currency: orderData.currency || 'INR',
                    order_id: orderData.orderId,
                    name: metadata.businessName || 'Visibeen',
                    description: metadata.description || 'Payment for services',
                    image: metadata.logo || '/logo.png',
                    prefill: {
                        name: metadata.userName || '',
                        email: metadata.userEmail || '',
                        contact: metadata.userPhone || ''
                    },
                    theme: {
                        color: metadata.themeColor || '#0B91D6'
                    }
                };

                this.openCheckout(
                    options,
                    async (response) => {
                        try {
                            // Step 3: Verify payment
                            const verificationResult = await this.verifyPayment(response);
                            resolve(verificationResult);
                        } catch (error) {
                            reject(error);
                        }
                    },
                    (error) => {
                        reject(error);
                    }
                );
            });

        } catch (error) {
            logger.error('[PaymentService] Payment processing error:', error);
            throw error;
        }
    }

    /**
     * Create a subscription with autopay mandate
     * @param {object} payload
     * @param {'basic'|'advance'|'pro'} payload.planId
     * @param {'monthly'|'annually'} payload.billingCycle
     * @param {string} payload.userEmail
     * @param {string} payload.userPhone
     * @param {object} [payload.notes]
     * @param {string} [payload.profileKey] - GMB profile key for tracking
     * @param {string} [payload.profileName] - GMB profile name
     * @param {string} [payload.accountId] - GMB account ID
     * @param {string} [payload.locationId] - GMB location ID
     * @returns {Promise<object>} Subscription details
     */
    async createSubscription({
        planId,
        billingCycle,
        userEmail,
        userPhone,
        userId,
        notes = {},
        addonAmount = 0,
        // GMB Profile info for syncing
        profileKey,
        profileName,
        accountId,
        locationId
    }) {
        try {
            logger.log('[PaymentService] Creating subscription:', { planId, billingCycle, userEmail, profileKey });

            // Normalize billingCycle to a clear form to avoid mismatches ('annually'|'yearly' -> 'yearly')
            let normalizedCycle = (billingCycle || '').toString().toLowerCase();
            if (/ann|year/.test(normalizedCycle)) normalizedCycle = 'yearly';
            else normalizedCycle = 'monthly';

            const response = await api.post('/customer/subscription/create', {
                planId,
                billingCycle: normalizedCycle,
                userEmail,
                userPhone,
                userId,
                notes,
                addonAmount,
                // Include GMB profile info for backend syncing
                profileKey,
                profileName,
                accountId,
                locationId
            });

            if (response.success) {
                logger.log('[PaymentService] Subscription created:', response.data.subscriptionId);
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to create subscription');
            }
        } catch (error) {
            logger.error('[PaymentService] Error creating subscription:', error);
            throw error;
        }
    }

    /**
     * Start trial order flow
        * POSTS to backend to create a ₹1 trial order and returns order details
     */
    async startTrial(planId, billingCycle = 'monthly', profileKey = null, userEmail = null, userPhone = null, trialTokenParam = null, profileName = null, accountId = null, locationId = null) {
        const { API_BASE_URL } = require('../config/api');
        const url = `${API_BASE_URL.replace(/\/api\/v1$/, '')}/api/v1/customer/subscription/start-trial`;
        // If profileKey/locationId is missing, include raw trialToken from localStorage as a fallback
        let trialToken = null;
        let trialSig = null;
        let trialLocation = null;
        try {
            if (typeof window !== 'undefined') {
                trialToken = window.localStorage.getItem('came_from_trial') || window.localStorage.getItem('came_from_trial_token') || null;
                trialSig = window.localStorage.getItem('came_from_trial_sig') || null;
                trialLocation = window.localStorage.getItem('came_from_trial_location') || null;
                // cookie fallback
                try {
                    const c = document.cookie.split(';').map(s => s.trim());
                    const t = c.find(x => x.startsWith('came_from_trial='));
                    const s = c.find(x => x.startsWith('came_from_trial_sig='));
                    const l = c.find(x => x.startsWith('came_from_trial_location='));
                    if (!trialToken && t) trialToken = decodeURIComponent(t.split('=')[1]);
                    if (!trialSig && s) trialSig = decodeURIComponent(s.split('=')[1]);
                    if (!trialLocation && l) trialLocation = decodeURIComponent(l.split('=')[1]);
                } catch (_) { }
            }
        } catch (_) { trialToken = null; trialSig = null; trialLocation = null; }

        // Normalize billingCycle to avoid ambiguous values
        let normalizedCycle = (billingCycle || '').toString().toLowerCase();
        if (/ann|year/.test(normalizedCycle)) normalizedCycle = 'yearly';
        else normalizedCycle = 'monthly';

        const body = {
            planId,
            billingCycle: normalizedCycle,
            profileKey,
            userEmail,
            userPhone,
            profileName,
            accountId,
            locationId
        };
        // If caller provided an explicit trial token, include it to help backend validation
        if (trialTokenParam) body.trialToken = trialTokenParam;
        // Always include any discovered trial context so backend can validate (preflight may have failed)
        if (trialToken) body.trialToken = trialToken;
        if (trialSig) body.sig = trialSig;
        // If we discovered a trialLocation use it; otherwise if profileKey looks like a location id, include as locationId too
        if (trialLocation) {
            body.locationId = trialLocation;
        } else if (profileKey) {
            body.locationId = profileKey;
        }
        // Get auth token from session storage
        let authToken = null;
        try {
            const session = JSON.parse(sessionStorage.getItem('session') || localStorage.getItem('session') || '{}');
            authToken = session.token || null;
        } catch (_) { authToken = null; }

        const headers = { 'Content-Type': 'application/json' };
        if (authToken) {
            headers['Authorization'] = `Bearer ${authToken}`;
        }

        const resp = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            credentials: 'include'
        });
        // Log request/response for debugging
        try {
            console.log('[PaymentService] startTrial request body:', body);
        } catch (e) { /* ignore */ }

        let data = null;
        let rawText = null;
        try {
            rawText = await resp.text();
            try { data = rawText ? JSON.parse(rawText) : null; } catch (e) { data = null; }
        } catch (e) {
            console.error('[PaymentService] Failed reading startTrial response body:', e);
        }

        console.log('[PaymentService] startTrial response status:', resp.status, 'parsed:', data, 'rawText:', rawText);

        if (!resp.ok) {
            const msg = (data && (data.message || data.error)) || rawText || `startTrial failed with status ${resp.status}`;
            console.error('[PaymentService] startTrial non-OK response:', { status: resp.status, body: data || rawText });
            throw new Error(msg);
        }

        if (!data || !data.success) {
            console.error('[PaymentService] startTrial failed response (no success):', data || rawText);
            throw new Error((data && data.message) || 'Failed to create trial subscription');
        }
        // Expecting { subscriptionId, keyId, amount, currency }
        // Ensure a Payment row exists on our backend for this deferred trial subscription
        try {
            const API_BASE = require('../config/api').API_BASE_URL.replace(/\/api\/v1$/, '');
            const ensureUrl = `${API_BASE}/api/v1/payments/ensure`;
            const subscriptionInfo = data.data || {};
            const orderId = `sub_order_${subscriptionInfo.subscriptionId}`;
            const ensureBody = {
                order_id: orderId,
                payment_id: null,
                receipt: `trial_sub_${subscriptionInfo.subscriptionId}`,
                amount: subscriptionInfo.amount || 1,
                currency: subscriptionInfo.currency || 'INR',
                status: 'created',
                notes: { subscription_id: subscriptionInfo.subscriptionId, type: 'subscription', checkout_initiated: true },
                gmb_profile_key: body && body.profileKey ? body.profileKey : null,
                customer_email: body && body.userEmail ? body.userEmail : (body && body.profileEmail ? body.profileEmail : null),
                customer_phone: body && body.userPhone ? body.userPhone : (body && body.profilePhone ? body.profilePhone : null)
            };
            // Fire-and-forget; include credentials so authenticated users can persist their own payments
            fetch(ensureUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(ensureBody) })
                .then(r => r.json().catch(() => null)).then(j => { console.log('[PaymentService] ensured trial payment', orderId, j); }).catch(e => { console.warn('[PaymentService] ensure call failed', e); });
        } catch (e) { console.warn('[PaymentService] ensure trial payment errored', e); }

        return data.data;
    }

    /**
     * Convenience: perform trial preflight, start trial on backend and open Razorpay checkout
     * Mirrors the pricing checkout flow so TrialLanding triggers identical backend logic.
     */
    async startTrialAndOpenCheckout({ planId, billingCycle = 'monthly', profileKey = null, userEmail = null, userPhone = null, metadata = {} }, onSuccess, onFailure) {
        try {
            // Resolve API base for preflight
            const { API_BASE_URL } = require('../config/api');
            const apiHost = API_BASE_URL.replace(/\/api\/v1$/, '');

            // Resolve trial token & location fallbacks (client may set these in localStorage or cookies)
            const trialToken = (typeof window !== 'undefined') ? (
                window.localStorage.getItem('came_from_trial') ||
                (() => { try { const m = document.cookie.split('; ').find(c => c.trim().startsWith('came_from_trial=')); return m ? m.split('=')[1] : null; } catch (_) { return null; } })() ||
                (() => { try { const u = new URL(window.location.href); return u.searchParams.get('trialToken') || u.searchParams.get('trialtoken'); } catch (_) { return null; } })()
            ) : null;

            let locationId = profileKey || null;
            try { if (!locationId) locationId = (typeof window !== 'undefined') ? (window.localStorage.getItem('came_from_trial_location') || null) : null; } catch (_) { locationId = locationId || null; }
            if (!locationId) {
                try { const u = new URL(window.location.href); locationId = u.searchParams.get('locationId') || u.searchParams.get('locationid') || u.searchParams.get('location'); } catch (_) { }
            }

            const sig = (typeof window !== 'undefined') ? (window.localStorage.getItem('came_from_trial_sig') || (() => { try { const m = document.cookie.split('; ').find(c => c.trim().startsWith('came_from_trial_sig=')); return m ? m.split('=')[1] : null; } catch (_) { return null; } })()) : null;

            if (!trialToken) {
                const details = { trialToken: trialToken || null, locationId: locationId || null, sig: sig || null };
                console.error('[PaymentService] Missing trial context:', details);
                throw new Error(`Missing trial token. Debug: ${JSON.stringify(details)}`);
            }

            // If we have a locationId, perform preflight to set httpOnly trial_ctx cookie on backend.
            // Otherwise rely on backend fallback that accepts a raw trialToken in the request body.
            if (locationId) {
                const ctxUrl = `${apiHost}/api/v1/gmb/trial/context?locationId=${encodeURIComponent(locationId)}&trialToken=${encodeURIComponent(trialToken)}${sig ? `&sig=${encodeURIComponent(sig)}` : ''}`;
                const ctxResp = await fetch(ctxUrl, { credentials: 'include' });
                if (!ctxResp.ok) {
                    const txt = await ctxResp.text().catch(() => '');
                    throw new Error(txt || 'Failed to validate trial context');
                }
            } else {
                console.warn('[PaymentService] No locationId found — will send trialToken in request body as fallback');
            }

            // Start trial backend flow (creates deferred subscription + returns razorpay subscription data)
            const subscriptionData = await this.startTrial(planId, billingCycle, locationId, userEmail, userPhone);

            // Open checkout
            this.openSubscriptionCheckout(subscriptionData, {
                userEmail,
                userPhone,
                profileKey: locationId,
                businessName: metadata.businessName || 'Visibeen',
                logo: metadata.logo || '/logo.png',
                themeColor: metadata.themeColor || '#0B91D6'
            }, (resp) => {
                if (onSuccess) onSuccess(resp);
            }, (err) => {
                if (onFailure) onFailure(err);
            });

            return subscriptionData;
        } catch (err) {
            console.error('[PaymentService] startTrialAndOpenCheckout error:', err);
            if (onFailure) onFailure(err);
            throw err;
        }
    }

    /**
     * Open Razorpay checkout for subscription with autopay
     * @param {object} subscriptionData - Data from createSubscription
     * @param {object} metadata - Additional metadata
     * @param {function} onSuccess - Success callback
     * @param {function} onFailure - Failure callback
     */
    openSubscriptionCheckout(subscriptionData, metadata = {}, onSuccess, onFailure) {
        // Prefer a trial-focused description when backend returned a trial verification amount
        const trialAmount = subscriptionData && (subscriptionData.amount || subscriptionData.amount === 0) ? Number(subscriptionData.amount) : null;
        const planLabel = subscriptionData && (subscriptionData.name) ? subscriptionData.name : (metadata.businessName || 'Visibeen');
        const defaultDescription = trialAmount ? `Trial: ₹${trialAmount} today (verification). Plan will be billed after the trial.` : (subscriptionData.description || 'Subscription with Autopay');

        const options = {
            key: subscriptionData.keyId,
            subscription_id: subscriptionData.subscription_id || subscriptionData.subscriptionId,
            name: subscriptionData.name || metadata.businessName || 'Visibeen',
            description: defaultDescription,
            image: metadata.logo || '/logo.png',
            prefill: subscriptionData.prefill || {
                email: metadata.userEmail || '',
                contact: metadata.userPhone || ''
            },
            theme: {
                color: metadata.themeColor || '#0B91D6'
            },
            // Enable recurring payments
            recurring: true,
            // Subscription card change allowed
            subscription_card_change: true,
            handler: (response) => {
                logger.log('[PaymentService] Subscription payment success:', response);
                if (onSuccess) onSuccess(response);
            },
            modal: {
                ondismiss: () => {
                    logger.log('[PaymentService] Subscription checkout dismissed');
                    if (onFailure) onFailure({ error: 'Payment cancelled by user' });
                }
            }
        };

        this.loadRazorpayScript()
            .then(() => {
                const rzp = new window.Razorpay(options);
                rzp.open();
            })
            .catch((err) => {
                logger.error('[PaymentService] Failed to load Razorpay:', err);
                if (onFailure) onFailure(err);
            });
    }

    /**
     * Process subscription payment with autopay mandate
     * @param {object} payload
     * @param {'basic'|'advance'|'pro'} payload.planId
     * @param {'monthly'|'annually'} payload.billingCycle
     * @param {object} payload.metadata
     * @param {string} [payload.metadata.profileKey] - GMB profile key
     * @param {string} [payload.metadata.profileName] - GMB profile name
     * @param {string} [payload.metadata.accountId] - GMB account ID
     * @param {string} [payload.metadata.locationId] - GMB location ID
     * @returns {Promise<object>} Subscription result
     */
    async processSubscription({ planId, billingCycle, metadata = {}, addonAmount = 0 }) {
        try {
            // Step 1: Create subscription with GMB profile info
            const subscriptionData = await this.createSubscription({
                planId,
                billingCycle,
                userEmail: metadata.userEmail,
                userPhone: metadata.userPhone,
                userId: metadata.userId,
                notes: metadata.notes || {},
                addonAmount,
                // Pass GMB profile info for syncing
                profileKey: metadata.profileKey,
                profileName: metadata.profileName || metadata.businessName,
                accountId: metadata.accountId,
                locationId: metadata.locationId
            });

            // Step 2: Open Razorpay subscription checkout
            return new Promise((resolve, reject) => {
                this.openSubscriptionCheckout(
                    subscriptionData,
                    metadata,
                    (response) => {
                        logger.log('[PaymentService] Subscription activated:', response);
                        resolve({
                            success: true,
                            subscriptionId: subscriptionData.subscriptionId,
                            paymentId: response.razorpay_payment_id,
                            signature: response.razorpay_signature
                        });
                    },
                    (error) => {
                        reject(error);
                    }
                );
            });

        } catch (error) {
            logger.error('[PaymentService] Subscription processing error:', error);
            throw error;
        }
    }

    /**
     * Get subscription details
     * @param {string} subscriptionId
     * @returns {Promise<object>}
     */
    async getSubscription(subscriptionId) {
        try {
            const response = await api.get(`/customer/subscription/${subscriptionId}`);
            return response.data;
        } catch (error) {
            logger.error('[PaymentService] Error fetching subscription:', error);
            throw error;
        }
    }

    /**
     * Cancel subscription
     * @param {string} subscriptionId
     * @param {boolean} cancelAtCycleEnd - If true, cancel at end of billing cycle
     * @returns {Promise<object>}
     */
    async cancelSubscription(subscriptionId, cancelAtCycleEnd = true) {
        try {
            const response = await api.post(`/customer/subscription/${subscriptionId}/cancel`, {
                cancelAtCycleEnd
            });
            return response.data;
        } catch (error) {
            logger.error('[PaymentService] Error cancelling subscription:', error);
            throw error;
        }
    }
}

export default new PaymentService();
